import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import { ReactTagManager } from "react-gtm-ts";
import axios from "axios";
import * as Definitions from "./definitions";
import {
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  Checkbox,
  Button,
  Box,
  ChakraProvider,
  Stack,
  Container,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Card,
  CardBody,
  Spinner,
  Center,
} from "@chakra-ui/react";

interface Profile {
  crawl_status: string;
  average_rating: number;
  review_count: number;
  last_crawl: string;
  source_url: string;
  status: string;
  job_id: string;
}

interface Review {
  _id: string;
  review_title: string;
  review_text: string;
  name: string;
  rating_value: number;
  date: string;
  status: string | null;
}

interface Settings {
  auto_publish: boolean;
  _save?: boolean;
}

interface ApiResponse<T> {
  type: string;
  object: T;
  reviews?: Review[];
}

interface ProfileResponse {
  success?: boolean;
  crawl_status?: string;
  average_rating?: number;
  review_count?: number;
  last_crawl?: string;
  source_url?: string;
  status?: string;
  job_id?: string;
  sample?: boolean;
}

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

function App() {
  ReactTagManager.init(tagManagerArgs);
  const intercomInitialized = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isReviewsLoading, setIsReviewsLoading] = useState(false);
  const [instance, setInstance] = React.useState({} as any);
  const [currentApp] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get("client_id");
    return Definitions.Apps[clientId || "google-reviews"];
  });
  const [appId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("client_id") || "google-reviews";
  });
  const [isAppReady, setIsAppReady] = useState(false);
  const BASE_URL = currentApp?.endpoint;
  const [profile, setProfile] = useState<Profile | null>(null);
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [showProfileCreatedNotification, setShowProfileCreatedNotification] =
    useState(false);
  const [, setShowSettingSavedNotification] =
    useState(false);
  const [
    ,
    setShowSettingSaveFailedNotification,
  ] = useState(false);
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [reviewsFilterVisibility] = useState("0");
  const [reviewsFilterStars] = useState("0");
  const [, setIsPinnedWidgetLoading] = useState(true);
  const [, setIsPinnedWidgetEnabled] = useState(false);

  useEffect(() => {
    if (!intercomInitialized.current && instance.intercomToken && instance.storeProfile && instance.app) {
      const name = instance.storeProfile.account.accountName;
      const registrationDate = new Date(
        instance.storeProfile.account.registrationDate
      );
      const created_at = Math.floor(registrationDate.getTime() / 1000);
      const phone = instance.storeProfile.company.phone;
      const email = instance.storeProfile.account.accountEmail;
      const utm_source = instance.app.client_id;
      const options = {
        email,
        name,
        created_at,
        phone,
        user_hash: instance.intercomToken,
        utm_source,
      };
      window.Intercom("boot", options);
      intercomInitialized.current = true;
    }
  }, [instance]);

  useEffect(() => {
    try {
      window.EcwidApp.init({
        app_id: appId,
        autoloadedflag: true,
        autoheight: false,
      });

      if (currentApp?.endpoint) {
        setIsAppReady(true);
        getInstance();
        getProfile();
        getSettings();
      }

      const updateHeight = () => {
        var body = document.body,
          html = document.documentElement;

        var height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );

        window.EcwidApp.setSize({ height: height });
      };

      updateHeight();

      const instanceIntervalId = setInterval(getInstance, 10000);
      const heightIntervalId = setInterval(updateHeight, 1000);

      return () => {
        clearInterval(instanceIntervalId);
        clearInterval(heightIntervalId);
      };
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (settings?._save === true) {
      delete settings._save;
      updateSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  useEffect(() => {
    const reloader = setInterval(() => {
      if (profile?.crawl_status !== "complete") {
        getProfile();
      }
    }, 30000);
    return () => clearInterval(reloader);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (currentApp) {
      checkPinnedWidgetStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentApp]);

  function getInstance() {
    if (!currentApp?.endpoint) return;

    fetch(
      BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/auth",
      {
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
      }
    )
      .then((response) => response.json())
      .then((payload) => {
        setInstance(payload);
        setIsLoading(false);
        window.EcwidApp.ready();
      })
      .catch((error) => {
        console.error("Error:", error);
        setIsLoading(false);
      });
  }

  function createProfile() {
    const api = axios.create({
      baseURL: currentApp.endpoint,
      headers: {
        Authorization:
          new URLSearchParams(window.location.search).get("payload") || "",
      },
    });

    console.log(profile);

    if (!profileUrl) return;

    console.log(profileUrl.split(" ").length);

    api
      .post<ApiResponse<ProfileResponse>>(
        "/_functions/@certifiedcode/base-reviews-backend/v1/createProfile",
        [
          {
            // blocks,
            // diff: profile?.crawl_status === "complete" ? profile?.job_id : null,
            priority_job: false,
            ...(appId === "google-reviews"
              ? (profileUrl.split(" ").length > 1
                ? {
                    query: profileUrl,
                  }
                : {
                    place_id: profileUrl.startsWith("https://")
                      ? new URL(profileUrl).searchParams.get("placeid")
                      : profileUrl,
                  })
              : { url: profileUrl }),
          },
        ]
      )
      .then((response) => {
        if (
          response?.data?.type === "profile" &&
          response?.data?.object?.success === true
        ) {
          setShowProfileCreatedNotification(true);
          getProfile();
        }
      })
      .catch(() => {});
  }

  function getProfile() {
    if (!currentApp?.endpoint) return;

    setIsProfileLoading(true);
    const api = axios.create({
      baseURL: currentApp.endpoint,
      headers: {
        Authorization:
          new URLSearchParams(window.location.search).get("payload") || "",
      },
    });

    api
      .post<ApiResponse<ProfileResponse>>(
        "/_functions/@certifiedcode/base-reviews-backend/v1/getProfile",
        []
      )
      .then((response) => {
        if (
          response?.data?.type === "profile" &&
          !response?.data?.object?.sample
        ) {
          setProfile(response.data.object as Profile);
          setProfileUrl(response?.data?.object?.source_url || null);
          getReviews();
        }
        setIsProfileLoading(false);
      })
      .catch(() => {
        setIsProfileLoading(false);
      });
  }

  function getReviews() {
    if (!currentApp?.endpoint) return;

    setIsReviewsLoading(true);
    const api = axios.create({
      baseURL: currentApp.endpoint,
      headers: {
        Authorization:
          new URLSearchParams(window.location.search).get("payload") || "",
      },
    });

    api
      .post<ApiResponse<never>>(
        "/_functions/@certifiedcode/base-reviews-backend/v1/getAllReviews",
        []
      )
      .then((response) => {
        if (response?.data?.type === "reviews") {
          setReviews(response.data.reviews || null);
        }
        setIsReviewsLoading(false);
      })
      .catch(() => {
        setIsReviewsLoading(false);
      });
  }

  function getSettings() {
    if (!currentApp?.endpoint) return;

    const api = axios.create({
      baseURL: currentApp.endpoint,
      headers: {
        Authorization:
          new URLSearchParams(window.location.search).get("payload") || "",
      },
    });

    api
      .post<ApiResponse<Settings>>(
        "/_functions/@certifiedcode/base-reviews-backend/v1/getSettings",
        []
      )
      .then((response) => {
        if (response?.data?.type === "settings") {
          setSettings(response.data.object);
        }
      })
      .catch((error) => {
        console.error("Error fetching settings:", error);
      });
  }

  function updateSettings() {
    if (!currentApp?.endpoint) return;

    const api = axios.create({
      baseURL: currentApp.endpoint,
      headers: {
        Authorization:
          new URLSearchParams(window.location.search).get("payload") || "",
      },
    });

    api
      .post<ApiResponse<Settings>>(
        "/_functions/@certifiedcode/base-reviews-backend/v1/updateSettings",
        [settings]
      )
      .then((response) => {
        if (response?.data?.type === "settings") {
          setSettings(response.data.object);
          setShowSettingSavedNotification(true);
        } else {
          setShowSettingSaveFailedNotification(true);
        }
      })
      .catch(() => {
        setShowSettingSaveFailedNotification(true);
      });
  }

  function checkPinnedWidgetStatus() {
    if (!currentApp?.endpoint) return;

    setIsPinnedWidgetLoading(true);
    const api = axios.create({
      baseURL: currentApp.endpoint,
      headers: {
        Authorization:
          new URLSearchParams(window.location.search).get("payload") || "",
      },
    });

    api
      .get("/_functions/@certifiedcode/base-reviews-backend/island")
      .then(() => {
        setIsPinnedWidgetEnabled(true);
        setIsPinnedWidgetLoading(false);
      })
      .catch((error: { response?: { status: number } }) => {
        if (error.response?.status === 404) {
          setIsPinnedWidgetEnabled(false);
        }
        setIsPinnedWidgetLoading(false);
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function togglePinnedWidget(enabled: boolean) {
    if (!currentApp?.endpoint) return;

    setIsPinnedWidgetLoading(true);
    const api = axios.create({
      baseURL: currentApp.endpoint,
      headers: {
        Authorization:
          new URLSearchParams(window.location.search).get("payload") || "",
      },
    });

    if (enabled) {
      api
        .post("/_functions/@certifiedcode/base-reviews-backend/island")
        .then(() => {
          setIsPinnedWidgetEnabled(true);
          setIsPinnedWidgetLoading(false);
        })
        .catch(() => {
          setIsPinnedWidgetLoading(false);
        });
    } else {
      api
        .delete("/_functions/@certifiedcode/base-reviews-backend/island")
        .then(() => {
          setIsPinnedWidgetEnabled(false);
          setIsPinnedWidgetLoading(false);
        })
        .catch(() => {
          setIsPinnedWidgetLoading(false);
        });
    }
  }

  function updateReview(reviewId: string, status: string | null) {
    if (!currentApp?.endpoint) return;

    const api = axios.create({
      baseURL: currentApp.endpoint,
      headers: {
        Authorization:
          new URLSearchParams(window.location.search).get("payload") || "",
      },
    });

    api
      .post<ApiResponse<Review>>(
        "/_functions/@certifiedcode/base-reviews-backend/v1/updateReview",
        [reviewId, { status: status === "AUTO" ? null : status }]
      )
      .then((response) => {
        if (response?.data?.type === "review") {
          setReviews(
            reviews?.map((item: Review) => {
              if (item._id === reviewId) {
                return response.data.object;
              }
              return item;
            }) || null
          );
        }
      });
  }

  return (
    <ChakraProvider>
      <Box bg={useColorModeValue("gray.50", "gray.800")} minH="100vh" py={5}>
        <Container maxW="container.xl">
          {isLoading || !isAppReady ? (
            <Center h="50vh">
              <VStack spacing={4}>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text color="gray.600">Loading your reviews dashboard...</Text>
              </VStack>
            </Center>
          ) : (
            <Stack spacing={8}>
              <Box>
                <Text fontSize="2xl" fontWeight="bold" mb={2}>
                  {currentApp.name} Reviews
                </Text>
                <Text color="gray.600">
                  Display {currentApp.name} reviews on your site
                </Text>
              </Box>

              {(showProfileCreatedNotification ||
                (profile && profile.crawl_status !== "complete")) && (
                <Card
                  bg={
                    profile?.crawl_status === "failed" ||
                    profile?.crawl_status === "invalid_url"
                      ? "red.50"
                      : "green.50"
                  }
                  border="1px"
                  borderColor={
                    profile?.crawl_status === "failed" ||
                    profile?.crawl_status === "invalid_url"
                      ? "red.200"
                      : "green.200"
                  }
                >
                  <CardBody>
                    <HStack>
                      <Box
                        color={
                          profile?.crawl_status === "failed" ||
                          profile?.crawl_status === "invalid_url"
                            ? "red.500"
                            : "green.500"
                        }
                      >
                        {profile?.crawl_status === "failed" ||
                        profile?.crawl_status === "invalid_url" ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            width="24"
                            height="24"
                          >
                            <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm-1-5h2v2h-2v-2zm0-8h2v6h-2V7z" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 70 70"
                          >
                            <path d="M34.5 67h-.13c-8.68-.03-16.83-3.45-22.94-9.61C5.32 51.23 1.97 43.06 2 34.38 2.07 16.52 16.65 2 34.5 2h.13c8.68.03 16.83 3.45 22.94 9.61 6.12 6.16 9.46 14.34 9.43 23.02C66.93 52.48 52.35 67 34.5 67zm0-62C18.3 5 5.06 18.18 5 34.39c-.03 7.88 3.01 15.3 8.56 20.89 5.55 5.59 12.95 8.69 20.83 8.72h.12c16.2 0 29.44-13.18 29.5-29.39.03-7.88-3.01-15.3-8.56-20.89C49.89 8.13 42.49 5.03 34.61 5h-.11z"></path>
                            <path d="M32.17 46.67l-10.7-10.08c-.6-.57-.63-1.52-.06-2.12.57-.6 1.52-.63 2.12-.06l8.41 7.92 14.42-16.81c.54-.63 1.49-.7 2.12-.16.63.54.7 1.49.16 2.12L32.17 46.67z"></path>
                          </svg>
                        )}
                      </Box>
                      <Text
                        color={
                          profile?.crawl_status === "failed" ||
                          profile?.crawl_status === "invalid_url"
                            ? "red.700"
                            : "green.700"
                        }
                      >
                        {profile?.crawl_status === "pending" &&
                          "Connection in progress..."}
                        {profile?.crawl_status === "complete" &&
                          "Connection successful"}
                        {profile?.crawl_status === "maintenance" &&
                          "Service under maintenance, please try again later"}
                        {profile?.crawl_status === "invalid_url" &&
                          "Invalid URL provided"}
                        {profile?.crawl_status === "failed" &&
                          "Connection failed, please try again"}
                      </Text>
                    </HStack>
                  </CardBody>
                </Card>
              )}

              {profile && profile?.crawl_status === "completed" && (
                <Card>
                  <CardBody>
                    {isProfileLoading ? (
                      <Center py={6}>
                        <VStack spacing={4}>
                          <Spinner size="md" color="blue.500" />
                          <Text color="gray.600">Loading profile data...</Text>
                        </VStack>
                      </Center>
                    ) : (
                      <VStack spacing={6} align="stretch">
                        <Box>
                          <Text fontSize="lg" fontWeight="semibold">
                            Review Statistics
                          </Text>
                        </Box>

                        <Box>
                          <Text fontSize="md" fontWeight="semibold">
                            Overall Rating
                          </Text>
                          <Text fontSize="xl">{profile?.average_rating}/5</Text>
                        </Box>

                        <Box>
                          <Text fontSize="md" fontWeight="semibold">
                            Total Reviews
                          </Text>
                          <Text fontSize="xl">{profile?.review_count}</Text>
                        </Box>

                        <Box>
                          <Text fontSize="md" fontWeight="semibold">
                            Last Updated
                          </Text>
                          <Text fontSize="xl">{profile?.last_crawl}</Text>
                        </Box>
                      </VStack>
                    )}
                  </CardBody>
                </Card>
              )}

              <Card>
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <Box>
                      <Text fontSize="lg" fontWeight="semibold">
                        Connect {currentApp.name} reviews
                      </Text>
                      <Text color="gray.600" fontSize="sm">
                        To display reviews, connect your {currentApp.name}{" "}
                        account.
                      </Text>
                    </Box>

                    <FormControl>
                      <FormLabel fontWeight="medium">
                        Connect {currentApp.name} reviews
                      </FormLabel>
                      <Input
                        value={profileUrl || ""}
                        onChange={(e) => setProfileUrl(e.target.value)}
                        placeholder={currentApp?.placeholders?.url}
                        size="md"
                        maxLength={256}
                        bg="white"
                        borderColor="gray.200"
                        _hover={{ borderColor: "gray.300" }}
                        _focus={{
                          borderColor: "blue.500",
                          boxShadow: "outline",
                        }}
                      />
                      {currentApp?.placeholders?.example && (
                        <FormHelperText color="gray.600">
                          Examples: {currentApp?.placeholders?.example}
                        </FormHelperText>
                      )}
                      {appId === "google-reviews" && (
                        <FormHelperText color="gray.600">
                          You can also{" "}
                          <a href="https://placeid-finder.certifiedcode.io/" target="_blank" rel="noopener noreferrer" style={{ color: '#3182CE' }}>
                            get your place ID here
                          </a>
                          {" "}and paste it directly into this input field.
                        </FormHelperText>
                      )}
                    </FormControl>

                    <Button
                      colorScheme="blue"
                      size="md"
                      isDisabled={
                        !profileUrl || profile?.source_url === profileUrl
                      }
                      onClick={createProfile}
                      width="fit-content"
                    >
                      {profile?.source_url
                        ? "Update Connection"
                        : "Connect Reviews"}
                    </Button>
                  </VStack>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text fontSize="lg" fontWeight="semibold">
                        Review Settings
                      </Text>
                      <Text color="gray.600" fontSize="sm">
                        Configure how reviews are displayed on your site
                      </Text>
                    </Box>

                    <FormControl>
                      <Checkbox
                        size="lg"
                        isChecked={settings?.auto_publish === true}
                        onChange={(e) => {
                          setSettings({
                            ...settings,
                            auto_publish: e.target.checked,
                            _save: true,
                          });
                        }}
                        colorScheme="blue"
                      >
                        Auto-publish new reviews
                      </Checkbox>
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>

              {reviews && reviews.length > 0 ? (
                <Card>
                  <CardBody>
                    {isReviewsLoading ? (
                      <Center py={6}>
                        <VStack spacing={4}>
                          <Spinner size="md" color="blue.500" />
                          <Text color="gray.600">Loading reviews...</Text>
                        </VStack>
                      </Center>
                    ) : (
                      <VStack spacing={4} align="stretch">
                        <HStack justify="space-between">
                          <Text fontSize="lg" fontWeight="semibold">
                            Reviews
                          </Text>
                          <HStack spacing={2}>
                            <Button
                              colorScheme="blue"
                              size="sm"
                              leftIcon={
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  width="16"
                                  height="16"
                                >
                                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" />
                                </svg>
                              }
                              isDisabled={profile?.crawl_status !== "complete"}
                              onClick={createProfile}
                            >
                              Refresh Reviews
                            </Button>
                            {profile?.review_count &&
                              profile.review_count > reviews.length &&
                              (instance?.instance?.billing?.packageName ? (
                                <Button
                                  colorScheme="blue"
                                  size="sm"
                                  leftIcon={
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      width="16"
                                      height="16"
                                    >
                                      <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" />
                                    </svg>
                                  }
                                  isDisabled={
                                    profile?.crawl_status !== "complete"
                                  }
                                  onClick={createProfile}
                                >
                                  Sync Reviews
                                </Button>
                              ) : instance?.store_id ? (
                                <Button
                                  colorScheme="purple"
                                  size="sm"
                                  leftIcon={
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      width="16"
                                      height="16"
                                    >
                                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 2.18l7 3.12v4.7c0 4.83-3.4 9.36-7 10.6-3.6-1.24-7-5.77-7-10.6V6.3l7-3.12z" />
                                      <path d="M11 7h2v2h-2zm0 4h2v6h-2z" />
                                    </svg>
                                  }
                                  onClick={() => {
                                    if (instance?.storeProfile) {
                                      window.open(
                                        encodeURI(
                                          "https://www.certifiedcode.us/upgrade/apps?checkout=" +
                                            JSON.stringify({
                                              settings: {
                                                allowLogout: false,
                                                allowDiscountRemoval: false,
                                              },
                                              items: [
                                                {
                                                  priceId: currentApp.priceId,
                                                  quantity: 1,
                                                },
                                              ],
                                              customer: {
                                                email:
                                                  instance.storeProfile.account
                                                    .accountEmail,
                                                business: {
                                                  name: instance.storeProfile
                                                    .account.accountName,
                                                },
                                                address: {
                                                  countryCode:
                                                    instance.storeProfile
                                                      .company.countryCode,
                                                  postalCode:
                                                    instance.storeProfile
                                                      .company.postalCode,
                                                  city: instance.storeProfile
                                                    .company.city,
                                                },
                                              },
                                              customData: {
                                                store_id: `${instance.store_id}`,
                                              },
                                            })
                                        ),
                                        "_blank"
                                      );
                                    }
                                  }}
                                >
                                  Upgrade to Sync More
                                </Button>
                              ) : (
                                <Spinner size="sm" color="purple.500" />
                              ))}
                            <Box
                              px={2}
                              py={1}
                              bg="gray.100"
                              borderRadius="full"
                            >
                              <Text fontSize="sm">{reviews.length}</Text>
                            </Box>
                          </HStack>
                        </HStack>

                        <Box overflowX="auto">
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                            }}
                          >
                            <thead>
                              <tr>
                                <th
                                  style={{
                                    textAlign: "left",
                                    padding: "12px",
                                    borderBottom: "1px solid",
                                    borderColor: "gray.200",
                                  }}
                                >
                                  Review
                                </th>
                                <th
                                  style={{
                                    textAlign: "left",
                                    padding: "12px",
                                    borderBottom: "1px solid",
                                    borderColor: "gray.200",
                                  }}
                                >
                                  Reviewer
                                </th>
                                <th
                                  style={{
                                    textAlign: "left",
                                    padding: "12px",
                                    borderBottom: "1px solid",
                                    borderColor: "gray.200",
                                  }}
                                >
                                  Rating
                                </th>
                                <th
                                  style={{
                                    textAlign: "left",
                                    padding: "12px",
                                    borderBottom: "1px solid",
                                    borderColor: "gray.200",
                                  }}
                                >
                                  Date
                                </th>
                                <th
                                  style={{
                                    textAlign: "left",
                                    padding: "12px",
                                    borderBottom: "1px solid",
                                    borderColor: "gray.200",
                                  }}
                                >
                                  Visibility
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {reviews
                                .filter((item: Review) => {
                                  if (reviewsFilterVisibility === "0")
                                    return true;
                                  if (reviewsFilterVisibility === "1")
                                    return item.status === null;
                                  if (reviewsFilterVisibility === "2")
                                    return item.status === "PUBLISHED";
                                  if (reviewsFilterVisibility === "3")
                                    return item.status === "HIDDEN";
                                  return true;
                                })
                                .filter((item: Review) => {
                                  if (reviewsFilterStars === "0") return true;
                                  return (
                                    item.rating_value ===
                                    Number(reviewsFilterStars)
                                  );
                                })
                                .map((review: Review) => (
                                  <tr key={review._id}>
                                    <td
                                      style={{
                                        padding: "12px",
                                        borderBottom: "1px solid",
                                        borderColor: "gray.200",
                                      }}
                                    >
                                      <Box>
                                        <Text fontWeight="semibold">
                                          {review.review_title}
                                        </Text>
                                        <Text color="gray.600">
                                          {review.review_text}
                                        </Text>
                                      </Box>
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        borderBottom: "1px solid",
                                        borderColor: "gray.200",
                                      }}
                                    >
                                      {review.name || (
                                        <Text color="gray.400">
                                          Not available
                                        </Text>
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        borderBottom: "1px solid",
                                        borderColor: "gray.200",
                                      }}
                                    >
                                      <HStack spacing={1}>
                                        {[...Array(5)].map((_, i) => (
                                          <Text
                                            key={i}
                                            color={
                                              i < review.rating_value
                                                ? "yellow.400"
                                                : "gray.200"
                                            }
                                          >
                                            ★
                                          </Text>
                                        ))}
                                      </HStack>
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        borderBottom: "1px solid",
                                        borderColor: "gray.200",
                                      }}
                                    >
                                      {review.date}
                                    </td>
                                    <td
                                      style={{
                                        padding: "12px",
                                        borderBottom: "1px solid",
                                        borderColor: "gray.200",
                                      }}
                                    >
                                      <Select
                                        value={review.status ?? "AUTO"}
                                        onChange={(e) => {
                                          updateReview(
                                            review._id,
                                            e.target.value
                                          );
                                        }}
                                        size="md"
                                        width="150px"
                                        bg="white"
                                        borderColor="gray.200"
                                      >
                                        <option value="AUTO">Auto</option>
                                        <option value="PUBLISHED">
                                          Published
                                        </option>
                                        <option value="HIDDEN">Hidden</option>
                                      </Select>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </Box>
                      </VStack>
                    )}
                  </CardBody>
                </Card>
              ) : (
                <Card>
                  <CardBody>
                    {isReviewsLoading ? (
                      <Center py={6}>
                        <VStack spacing={4}>
                          <Spinner size="md" color="blue.500" />
                          <Text color="gray.600">Loading reviews...</Text>
                        </VStack>
                      </Center>
                    ) : (
                      <VStack py={8} spacing={2}>
                        <Text fontSize="xl" fontWeight="semibold">
                          No Reviews Yet
                        </Text>
                        <Text color="gray.600">
                          Connect your account to start displaying reviews
                        </Text>
                      </VStack>
                    )}
                  </CardBody>
                </Card>
              )}
            </Stack>
          )}
        </Container>
      </Box>
    </ChakraProvider>
  );
}

export default App;
